import { Box, Container } from '@mui/material';
import {
  authContext,
  EmployerSignUpModal,
  SignInModal,
  useGetProfile,
} from '@staffedupusa/shared-components';
import { useContext, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Outlet, useLocation } from 'react-router-dom';

import Sidebar from '@components/Layout/Sidebar/Sidebar';
import { useGetSidebarMenuItems } from '@components/Layout/Sidebar/useGetSidebarMenuItems';
import { lightGrey } from '@styles/colors';
import 'cropperjs/dist/cropper.css';
import CompanyExpiryModal from './CompanyExpiryModal/CompanyExpiryModal';
import FlagCompanyModal from './FlagCompanyModal/FlagCompanyModal';
import DisabledCompanyModal from './DisabledCompanyModal/DisabledCompanyModal';
import { trackViewedScreen } from '@utils/amplitudeTrackViewdScreen';
import RemoveAccessModal from './RemoveAccessModal/RemoveAccessModal';
import { init } from '@amplitude/analytics-browser';
import { selectedPlanName } from '@utils/pricing';

const App = () => {
  const { authData } = useContext(authContext);
  const location = useLocation();
  const isCurrentPage = location.pathname;
  const [isExpiryModalOpen, setIsExpiryModalOpen] = useState(false);
  const [isCompanyFlagModal, setIsCompanyFlagModal] = useState(false);
  const [isCompanyDisabledModal, setIsCompanyDisabledModal] = useState(false);
  const [removeAccessOnModal, setRemoveAccessOnModal] = useState(false);
  const [titles, setTitles] = useState(['', '']);
  const userID = authData?.user?.id;
  const userCreatedAt = new Date(authData?.user?.dt_timestamp || Date.now());
  const thresholdDate = new Date('March 03, 2025 00:00 AM UTC');
  const titlesRef = useRef(titles);
  const title = document.title;
  const user = authData?.user;
  const company = user?.company;
  const enterprise = user?.enterprise;

  const titleMap: Record<string, string> = {
    'Dashboard | StaffedUp': 'Applicants',
    'Position Management | StaffedUp': 'Job_Management',
    'Digital Onboarding | StaffedUp': 'Onboarding',
  };

  const screenPrefix = titleMap[title] || title;
  const userPlan = selectedPlanName(company?.plan ?? 0);

  useGetProfile();
  const sidebarMenuItems = useGetSidebarMenuItems(
    company
      ? 'employer'
      : enterprise
        ? 'enterprise'
        : authData.user?.is_rockerbox
          ? 'rockerbox'
          : authData.user?.is_admin
            ? 'admin'
            : 'applicant',
  );

  // LEAD_CONNECTOR_CHAT_WIDGET
  useEffect(() => {
    if (company || enterprise) {
      const script = document.createElement('script');
      script.src = 'https://widgets.leadconnectorhq.com/loader.js';
      script.setAttribute(
        'data-resources-url',
        'https://widgets.leadconnectorhq.com/chat-widget/loader.js',
      );
      script.setAttribute(
        'data-widget-id',
        import.meta.env.VITE_LEAD_CONNECTOR_ID,
      );
      script.async = true;
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
  }, [authData]);

  useEffect(() => {
    if (
      company &&
      company?.plan === 0 &&
      isCurrentPage !== '/employer/account'
    ) {
      setIsExpiryModalOpen(true);
    } else if (
      company &&
      company?.is_suspicious &&
      company?.plan !== 0
    ) {
      setIsCompanyFlagModal(true);
    } else if ((company || enterprise)  && !user?.enabled) {
      setIsCompanyDisabledModal(true);
    } else if (
      company &&
      company.plan === 999 &&
      !user?.walkthrough_completed &&
      isCurrentPage !== '/dashboard' &&
      isCurrentPage !== '/dashboard/' &&
      userCreatedAt > thresholdDate
    ) {
      setRemoveAccessOnModal(true);
    }
  }, [authData, isCurrentPage]);

  // AMPLITUDE INITILIZE FOR COMPANY AND ENTERPRISE USERS
  useEffect(() => {
    if (company || enterprise) {
      init(import.meta.env.VITE_AMPLITUDE_API_KEY, {
        defaultTracking: {
          sessions: false,
          pageViews: false,
        },
      });
    }
  }, [userID]);
  useEffect(() => {
    titlesRef.current = titles; // Keep ref updated with the latest titles
  }, [titles]);

  useEffect(() => {
    setTitles(([_, curr]) => [curr, title]); // Update titles
    if (company || enterprise) {
      const previousTitle = titlesRef.current[0] !== title
        ? titlesRef.current[0]
        : document.referrer;
      trackViewedScreen(
        `${screenPrefix}_${userPlan}`,
        String(user?.id),
        previousTitle
      );
    }
  }, [userID, document.title]);
  

  return (
    <>
      <Helmet>
        <title>Dashboard | StaffedUp</title>
      </Helmet>
      <Box
        bgcolor={lightGrey}
        display="flex"
        overflow="hidden"
        minHeight="100vh"
      >
        {authData.isAuth && authData.user && (
          <Sidebar menuItems={authData.user ? sidebarMenuItems : null} />
        )}
        <Container maxWidth={false} disableGutters>
          <Outlet />
        </Container>
      </Box>
      <EmployerSignUpModal />
      {isExpiryModalOpen && (
        <CompanyExpiryModal
          isExpiryModalOpen={isExpiryModalOpen}
          handleClose={() => {
            setIsExpiryModalOpen(false);
          }}
        />
      )}
      {isCompanyFlagModal && (
        <FlagCompanyModal
          isCompanyFlagModal={isCompanyFlagModal}
          handleClose={() => {
            setIsCompanyFlagModal(false);
          }}
        />
      )}
      {isCompanyDisabledModal && (
        <DisabledCompanyModal
          isCompanyDisabledModal={isCompanyDisabledModal}
          handleClose={() => {
            setIsCompanyDisabledModal(false);
          }}
        />
      )}
      {removeAccessOnModal && (
        <RemoveAccessModal
          removeAccessOnModal={removeAccessOnModal}
          handleClose={() => {
            setRemoveAccessOnModal(false);
          }}
        />
      )}
      <SignInModal />
    </>
  );
};

export default App;
